import { ActionContextType, ActionsContext, MxAction } from '../../Shell/Pandora/model/action';

import type { IComponentController } from 'angular';
import template from './dashboard-control-panel.component.html';
import type { I18n } from '../../i18n/mx-internationalization';
import { DashboardPageComponent, DashboardPageController } from '../dashboard-page/dashboard-page.component';
import type { MxActionManagerService } from '../../Shell/Pandora/services/mx-action-manager-service';
import type { AutoRefreshInterval, Dashboard, DashboardService } from '../dashboard.service';
import type { NotificationService } from '../../App/services/mx-notification-service';
import type { DataModelCacheService } from '../../Shell/PandoraSolutionBuilderAgent/services/data-model-cache.service';
import type { ConfigService } from '../../App/services/mx-config-service';
import type { WizardService } from '../../Shell/PandoraSolutionBuilderAgent/services/wizard-service';
import { WizardMode } from '../../Shell/PandoraSolutionBuilderAgent/model/enumerations';
import type { SidePanelService } from '../../Shell/Pandora/services/mx-side-panel-service';
import type { EntityService } from '../../Shell/PandoraSolutionBuilderAgent/services/entity-service';
import { DashboardExportWizardController } from '../dashboard-export-wizard/dashboard-export-wizard.component';
import dashboardExportWizardTemplate from '../dashboard-export-wizard/dashboard-export-wizard.component.html';
import { DashboardPrintWizardController } from '../dashboard-print-wizard/dashboard-print-wizard.component';
import dashboardPrintWizardTemplate from '../dashboard-print-wizard/dashboard-print-wizard.component.html';
import type { AnyObject } from '../../Types';
import type { UserProfileService } from '../../App/services/user-profile-service';
import type { MxNavigationService } from '../../Navigation/mx-navigation-service';
import { DisplayMode } from '../../Shell/Pandora/model/display-modes';
import { EventCodes } from '../../App/model/event-codes';
import type { EventService } from '../../App/services/event-service';
import { FeatureTrackingType, FeatureTrackingUseCases } from '../../Shell/PandoraSolutionBuilderAgent/model/enumerations';
import type { MxFeatureTrackingService } from '../../App/services/mx-feature-tracking-service';

interface AutoRefreshOption {
	Title: string;
	Value: AutoRefreshInterval;
}

export class DashboardControlPanelController implements IComponentController {
	page!: DashboardPageController;
	actions: MxAction[] = [];
	autoRefreshOptions: AutoRefreshOption[] = [];

	static readonly $inject = [
		'mx.internationalization',
		'mx.shell.ActionManagerService',
		'mx.sb.Dashboard.Service',
		'mx.shell.NotificationService',
		'mx.SolutionBuilderAgent.DataModelCacheService',
		'mx.shell.Config',
		'mx.SolutionBuilderAgent.WizardService',
		'mx.shell.SidePanelService',
		'mx.SolutionBuilderAgent.EntityService',
		'mx.shell.UserProfileService',
		'mx.shell.NavigationService',
		'mx.shell.EventService',
		'mx.shell.FeatureTrackingService',
	];

	constructor(
		private readonly i18n: I18n,
		private readonly actionManagerService: MxActionManagerService,
		private readonly dashboardService: DashboardService,
		private readonly notificationService: NotificationService,
		private readonly dataModelCacheService: DataModelCacheService,
		private readonly configService: ConfigService,
		private readonly wizardService: ReturnType<typeof WizardService>,
		private readonly sidePanelService: ReturnType<typeof SidePanelService>,
		private readonly entityService: EntityService,
		private readonly userProfileService: UserProfileService,
		private readonly navigationService: MxNavigationService,
		private readonly eventService: EventService,
		private readonly featureTrackingService: MxFeatureTrackingService
	) {
		this.autoRefreshOptions = [
			{
				Title: this.i18n.get('shell.common.off'),
				Value: 0,
			},
			{
				Title: this.i18n.getFormatted('shell.dashboards.autoRefresh.everyMinutes', {
					minutes: 30,
				}),
				Value: 30,
			},
			{
				Title: this.i18n.get('shell.dashboards.autoRefresh.everyHour'),
				Value: 60,
			},
			{
				Title: this.i18n.getFormatted('shell.dashboards.autoRefresh.everyHours', {
					hours: 2,
				}),
				Value: 120,
			},
			{
				Title: this.i18n.getFormatted('shell.dashboards.autoRefresh.everyHours', {
					hours: 4,
				}),
				Value: 240,
			},
			{
				Title: this.i18n.get('shell.dashboards.autoRefresh.everyDay'),
				Value: 1440,
			},
		];

		this.initSwitchingToNeighborDashboardsHandlers();
	}

	$onInit() {
		this.getActions().then((actions) => (this.actions = actions));
	}

	$onDestroy() {
		this.dataModelCacheService.enable();
	}

	private async initActions() {
		this.actions = await this.getActions();
	}

	async onSaveClick() {
		await this.page.disableConfigurationMode();
		await this.initActions();
		this.dataModelCacheService.enable();
	}

	private async showConfirmation(confirmationTitle: string, confirmationMessage: string): Promise<boolean> {
		try {
			await this.notificationService.confirm(confirmationTitle, confirmationMessage);
			return true;
		} catch {
			return false;
		}
	}

	private getAction(action: MxAction | undefined, exec: () => void, popover?: string, onPopoverClose?: () => void) {
		if (!action) {
			return null;
		}

		const execute = async () => {
			const isConfirmed =
				!action.serviceOperation?.ConfirmationMessage ||
				(await this.showConfirmation(action.customConfirmationTitle ?? action.title, (action.serviceOperation?.ConfirmationMessage || '') as string));

			if (isConfirmed) {
				exec();
			}
		};

		return this.createAction({
			name: action.name,
			title: action.title,
			icon: action.icon,
			execute,
			popover: popover,
			onPopoverClose: onPopoverClose,
		});
	}

	private getActionByPrefix(actions: MxAction[], prefix: string, exec: () => void, popover?: string, onPopoverClose?: () => void) {
		const action = this.getOriginalAction(actions, prefix);
		return action ? this.getAction(action, exec, popover, onPopoverClose) : null;
	}

	private getOriginalAction(actions: MxAction[], prefix: string) {
		return actions.find((action) => action.name.startsWith(prefix));
	}

	private getOpenWizardAction(actions: MxAction[], prefix: string, dashboard: Dashboard) {
		const action = this.getOriginalAction(actions, prefix);
		const wizardId = action?.Widget?.Id;
		if (!wizardId) {
			console.error('No wizard found for action:', action);
			return null;
		}

		return this.getAction(action, async () => {
			await this.showWizard({
				navigationId: dashboard.navigationId,
				widgetId: wizardId,
				widgetName: action.title,
				actionName: action.name,
				presetFields: {
					Context: {
						DashboardData: {
							filters: dashboard.filters.values,
							title: dashboard.title ?? '',
							navigationId: dashboard.navigationId,
						},
					},
				},
			});
		});
	}

	private getSubscribeAction(actions: MxAction[], dashboard: Dashboard) {
		return this.getOpenWizardAction(actions, 'UUX.Client.Dashboard.Subscribe', dashboard);
	}

	private getPrintAction(actions: MxAction[], dashboard: Dashboard) {
		return this.getActionByPrefix(actions, 'UUX.Client.Dashboard.Print', async () => {
			await this.notificationService.customDialog({
				controllerAs: 'vm',
				controller: DashboardPrintWizardController,
				template: dashboardPrintWizardTemplate,
				locals: {
					dashboard,
				},
				bindToController: true,
			});
		});
	}

	private getExportAction(actions: MxAction[], dashboard: Dashboard) {
		return this.getActionByPrefix(actions, 'UUX.Client.Dashboard.Export', async () => {
			await this.notificationService.customDialog({
				controllerAs: 'vm',
				controller: DashboardExportWizardController,
				template: dashboardExportWizardTemplate,
				locals: {
					dashboard,
				},
				bindToController: true,
			});
		});
	}

	private getSendAction(actions: MxAction[], dashboard: Dashboard) {
		return this.getOpenWizardAction(actions, 'UUX.Client.Dashboard.SendReport', dashboard);
	}

	private getShareAction(actions: MxAction[], dashboard: Dashboard) {
		return !dashboard.owner || dashboard.isOwner ? this.getOpenWizardAction(actions, 'UUX.Client.Dashboard.Share', dashboard) : null;
	}

	private getForceReloadAction(actions: MxAction[]) {
		const isRefreshPopoverHidden = this.dashboardService.isRefreshPopoverHidden();

		return this.getActionByPrefix(
			actions,
			'UUX.Client.Dashboard.ForceReload',
			async () => {
				await this.dataModelCacheService.resetCache();
				this.page.reloadPage();
			},
			isRefreshPopoverHidden ? undefined : this.i18n.getFormatted('shell.dashboards.cacheHint', this.configService.get('dataSourcesCacheLifetime')),
			isRefreshPopoverHidden ? undefined : () => this.dashboardService.setRefreshPopoverHidden()
		);
	}

	private async getDeleteAction(actions: MxAction[], dashboard: Dashboard) {
		const action = this.getOriginalAction(actions, 'UUX.Client.Dashboard.Delete');

		if (!action) {
			return null;
		}

		const user = await this.userProfileService.get();
		if (user.isDevMode || (dashboard.owner && (dashboard.isOwner || user.isAdmin))) {
			action.customConfirmationTitle = this.i18n.getFormatted('solutionBuilderAgent.common.deleteTemplate', dashboard.title);
			return this.getAction(action, async () => {
				const parentItem = (await this.navigationService.getNavigationItem(dashboard.navigationId))?.parent;
				await this.dashboardService.deleteDashboard(dashboard);
				const index = parentItem?.children?.findIndex((n) => n.id === dashboard.navigationId) ?? -1;
				await this.navigationService.setNavigationItemById(parentItem?.children?.[index - 1]?.id ?? parentItem?.id ?? this.navigationService.navItems[0].id);
				this.navigationService.removeItem(dashboard.navigationId, parentItem);
			});
		} else {
			return null;
		}
	}

	private getRemovePersonalizationAction(actions: MxAction[], dashboard: Dashboard) {
		return this.dashboardService.hasPersonalization(dashboard)
			? this.getActionByPrefix(actions, 'UUX.Client.Dashboard.RemovePersonalization', async () => {
					await this.dashboardService.removePersonalization(dashboard);
					this.page.reloadPage();
				})
			: null;
	}

	private getRevertPublishAction(actions: MxAction[], dashboard: Dashboard) {
		return dashboard.isCustomized
			? this.getActionByPrefix(actions, 'UUX.Client.Dashboard.RevertPublishedLayout', async () => {
					await this.dashboardService.removeCustomization(dashboard);
					this.page.reloadPage();
				})
			: null;
	}

	private getPublishAction(actions: MxAction[], dashboard: Dashboard) {
		return this.dashboardService.hasPersonalization(dashboard)
			? this.getActionByPrefix(actions, 'UUX.Client.Dashboard.PublishLayout', async () => {
					await this.dashboardService.publishDashboard(dashboard);
					this.page.reloadPage();
				})
			: null;
	}

	private getSwitchBackAction(actions: MxAction[], dashboard: Dashboard) {
		return this.getActionByPrefix(actions, 'UUX.Client.Dashboard.SwitchBack', () => this.dashboardService.dashboardLookOperation(dashboard.navigationId));
	}

	private getFullScreenAction(actions: MxAction[]) {
		return this.getActionByPrefix(actions, 'UUX.Client.Dashboard.FullScreen', () => {
			this.page.setDisplayMode(DisplayMode.BigScreen);
			this.eventService.dispatchEvent(EventCodes.TrackTelemetry, {
				eventName: 'Dashboard fullscreen mode enabled',
			});
		});
	}

	private getEditAction(actions: MxAction[]) {
		return this.getActionByPrefix(actions, 'UUX.Client.Dashboard.Edit', async () => {
			this.page.enableConfigurationMode();
			this.dataModelCacheService.disable();
		});
	}

	async getActions() {
		const actions = this.actionManagerService.filterActions(await this.actionManagerService.getActions(new ActionsContext([], []), [ActionContextType.None]));
		const dashboard = this.page.dashboard;

		return [
			this.getEditAction(actions),
			this.getFullScreenAction(actions),
			this.getSwitchBackAction(actions, dashboard),
			this.getPublishAction(actions, dashboard),
			this.getRevertPublishAction(actions, dashboard),
			this.getRemovePersonalizationAction(actions, dashboard),
			this.getForceReloadAction(actions),
			this.getShareAction(actions, dashboard),
			this.getSendAction(actions, dashboard),
			this.getExportAction(actions, dashboard),
			this.getPrintAction(actions, dashboard),
			this.getSubscribeAction(actions, dashboard),
			await this.getDeleteAction(actions, dashboard),
		].filter((x) => x !== null);
	}

	private async showWizard({
		navigationId,
		widgetId,
		widgetName,
		actionName,
		presetFields,
	}: {
		navigationId: string;
		widgetId: string;
		widgetName: string;
		actionName: string;
		presetFields?: AnyObject;
	}) {
		const objDetails = await this.entityService.getFragmentDescriptorsInBatch('PDRNavigationItemClassBase', navigationId);
		if (objDetails) {
			const navObjectId = objDetails.ObjectId;

			const wizardView = this.wizardService.getWizardView({
				widgetId: widgetId,
				wizardMode: WizardMode.Default,
				name: widgetName,
				displayName: widgetName,
				selectedObjects: [
					{
						_id: navObjectId,
						_type: 'PDRNavigationItemType',
						_displayName: this.page.dashboard.title,
					},
				],
				actionName: actionName,
				presetFields,
			});

			// @ts-expect-error no types
			wizardView.id = Date.now();
			this.sidePanelService.addView(wizardView);
		}
	}

	private createAction({
		name,
		title,
		icon,
		execute,
		popover,
		onPopoverClose,
	}: {
		name: string;
		title: string;
		icon: string;
		popover?: string;
		execute: () => Promise<void>;
		onPopoverClose?: () => void;
	}) {
		const action = new MxAction(name, title, icon);
		action.popover = popover;
		if (onPopoverClose) {
			action.onPopoverClose = onPopoverClose;
		}
		action.controller = function () {
			return {
				execute,
			};
		};
		return action;
	}

	async onAutoRefreshOptionChanged() {
		await this.page.setAutoRefresherInterval(this.page.dashboard.autoRefreshInterval);
		this.eventService.dispatchEvent(EventCodes.TrackTelemetry, {
			eventName: 'Dashboard auto-refresh enabled',
			intervalHours: this.page.dashboard.autoRefreshInterval / 60,
		});
	}

	initSwitchingToNeighborDashboardsHandlers() {
		this.eventService.subscribeOnce(EventCodes.DashboardPageInitialized, async () => {
			this.featureTrackingService.track(FeatureTrackingType.viewReport, FeatureTrackingUseCases.reportingToolkit);
		});
	}
}

export const DashboardControlPanelComponent = {
	name: 'mxDashboardControlPanel',
	controller: DashboardControlPanelController,
	controllerAs: 'vm',
	template,
	require: {
		page: `^${DashboardPageComponent.name}`,
	},
};
